/**
 * Session persistence for useChatApp: per-user saved threads, the active
 * session, sidebar preference and new/select/delete chat. Split out of
 * useChatApp.js unchanged (2026-09-25 gap closure).
 */
import { useEffect } from 'react';
import {
  loadSessions,
  getOrCreateActiveSession,
  createSession,
  upsertSession,
  setActiveSessionId,
  deleteSession,
  deriveSessionTitle,
  hasSessionUser,
} from '../../utils/chatSessions';
import { sanitizeMessagesForPersist } from '../../utils/confirmationCards';
import { safeStorage } from '../../utils/safeStorage';
import { placeFromMessages } from './chatConstants';

/**
 * @param {object} s - Context from useChatState.
 * @param {{ userId: string|null }} opts
 * @param {{ abortVoiceRecording: Function, discardRecordingForChatSwitch: Function }} voice
 */
export function useChatSessions(s, { userId }, { abortVoiceRecording, discardRecordingForChatSwitch }) {
  const {
    setSessions, sessionId, setSessionId, messages, setMessages, setInputMessage,
    isLoading, awaitingLocationRef, chatEpochRef, endTurn, clientLocation, setClientLocation,
    sessionPlace, setSessionPlace, setLocationError, setVoiceError, submittingConfirmationIdRef,
    reconnectRef, setSidebarCollapsed, sessionIdRef, skipPersistRef, loadedForUserRef,
  } = s;

  // Keep sessionIdRef in sync so sendMessage always uses the active session.
  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId, sessionIdRef]);

  // Persist only for signed-in users (skipped while switching sessions).
  useEffect(() => {
    if (!hasSessionUser(userId) || !sessionId) return;
    if (loadedForUserRef.current !== userId) return;
    if (skipPersistRef.current) {
      skipPersistRef.current = false;
      return;
    }
    const title = deriveSessionTitle(messages);
    // Never persist a pending/submitting card as confirmable — a reload must
    // not resurrect something the person could tap to submit (see
    // sanitizeMessagesForPersist docstring). The in-memory `messages` state
    // driving the live UI is untouched.
    const nextSessions = upsertSession(userId, {
      id: sessionId,
      title,
      messages: sanitizeMessagesForPersist(messages),
      clientLocation: clientLocation || null,
      sessionPlace: sessionPlace || null,
    });
    setSessions(nextSessions);
    setActiveSessionId(userId, sessionId);
  }, [messages, sessionId, userId, clientLocation, sessionPlace, loadedForUserRef, skipPersistRef, setSessions]);

  // On sign-in: restore that user's history. On sign-out: wipe the UI clean.
  useEffect(() => {
    skipPersistRef.current = true;
    loadedForUserRef.current = hasSessionUser(userId) ? userId : null;
    // The microphone never outlives the account that opened it (pass 9 F2).
    abortVoiceRecording();
    if (!hasSessionUser(userId)) {
      setSessions([]);
      setSessionId(null);
      setMessages([]);
      setInputMessage('');
      setClientLocation(null);
      setSessionPlace(null);
      setLocationError(null);
      chatEpochRef.current += 1;
      endTurn();
      return;
    }
    chatEpochRef.current += 1;
    // Signed in: the previous account's voice notice (including "sign in
    // again") no longer applies (pass 15). Sign-out keeps it on purpose.
    setVoiceError(null);
    const active = getOrCreateActiveSession(userId);
    setSessions(loadSessions(userId));
    setSessionId(active.id);
    // Re-sanitize on restore: stored copies are not trusted to be
    // non-confirmable (review B3 F1, 2026-09-29).
    setMessages(sanitizeMessagesForPersist(active.messages || []));
    setClientLocation(active.clientLocation || null);
    setSessionPlace(active.sessionPlace || placeFromMessages(active.messages) || null);
    endTurn();
    // Both callbacks are stable ([] deps), so listing them changes nothing;
    // it satisfies exhaustive-deps for the eslint-enabled build (2026-09-25 gap closure).
  }, [userId, abortVoiceRecording, endTurn, skipPersistRef, loadedForUserRef, chatEpochRef, setVoiceError, setSessions, setSessionId, setMessages, setClientLocation, setSessionPlace, setInputMessage, setLocationError]);

  /** Collapses/expands the session sidebar and persists preference. */
  const toggleSidebar = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      safeStorage.set('guardianSidebarCollapsed', String(next));
      return next;
    });
  };

  /**
   * A turn still running on the socket would deliver its reply (for a confirm:
   * the report outcome) into whichever thread is open when it arrives. Before
   * switching threads mid-turn, replace the socket so the old turn's events are
   * dropped; its card persists as 'unknown' (see sanitizeMessagesForPersist).
   */
  const detachInFlightTurn = () => {
    const sentFrame = isLoading && !awaitingLocationRef.current;
    if ((sentFrame || submittingConfirmationIdRef.current) && reconnectRef.current) {
      reconnectRef.current();
    }
  };

  /** Starts a blank session and clears composer state. */
  const handleNewChat = () => {
    detachInFlightTurn();
    discardRecordingForChatSwitch();
    chatEpochRef.current += 1;
    if (!hasSessionUser(userId)) {
      setSessionId(null);
      setSessions([]);
      setMessages([]);
      setInputMessage('');
      setClientLocation(null);
      setLocationError(null);
      endTurn();
      return;
    }
    const session = createSession(userId);
    setSessions(loadSessions(userId));
    setSessionId(session.id);
    setMessages([]);
    setInputMessage('');
    setClientLocation(null);
    setSessionPlace(null);
    setLocationError(null);
    endTurn();
  };

  /** Loads a saved session's messages into the main thread. */
  const handleSelectSession = (id) => {
    if (!hasSessionUser(userId)) return;
    // Re-selecting the open chat is not a switch: it must not kill a live
    // recording, reply audio, a pending card or a confirm in flight (pass 13).
    if (id === sessionId) return;
    const list = loadSessions(userId);
    const session = list.find((s) => s.id === id);
    if (!session) return;
    detachInFlightTurn();
    discardRecordingForChatSwitch();
    chatEpochRef.current += 1;
    skipPersistRef.current = true;
    setActiveSessionId(userId, id);
    setSessionId(id);
    setMessages(sanitizeMessagesForPersist(session.messages || [])); // B3 F1
    setInputMessage('');
    setClientLocation(session.clientLocation || null);
    setSessionPlace(session.sessionPlace || placeFromMessages(session.messages) || null);
    setLocationError(null);
    endTurn();
  };

  /** Removes a session; selects the next available one or clears the thread. */
  const handleDeleteSession = (id) => {
    if (!hasSessionUser(userId)) return;
    if (id !== sessionId) {
      // Deleting another thread must not touch the open one or its turn.
      deleteSession(userId, id);
      setSessions(loadSessions(userId));
      return;
    }
    detachInFlightTurn();
    discardRecordingForChatSwitch();
    chatEpochRef.current += 1;
    skipPersistRef.current = true;
    const next = deleteSession(userId, id);
    setSessions(loadSessions(userId));
    if (next) {
      setSessionId(next.id);
      setMessages(sanitizeMessagesForPersist(next.messages || [])); // B3 F1
      setClientLocation(next.clientLocation || null);
      setSessionPlace(next.sessionPlace || null);
    } else {
      setSessionId(null);
      setMessages([]);
      setClientLocation(null);
      setSessionPlace(null);
    }
    setInputMessage('');
    setLocationError(null);
    endTurn();
  };

  return {
    toggleSidebar,
    handleNewChat,
    handleSelectSession,
    handleDeleteSession,
  };
}
